import {useEffect, useRef} from 'react';

export interface SerialPollingOptions {
    enabled: boolean;
    delayMs: number;
    epoch: number;

    task(signal: AbortSignal): Promise<void>;
}

export function useSerialPolling({enabled, delayMs, task, epoch}: SerialPollingOptions): void {
    const timerRef = useRef<number | null>(null);
    const taskRef = useRef(task);
    taskRef.current = task;

    useEffect(() => {
        if (!enabled) return;
        const controller = new AbortController();
        let disposed = false;
        const run = async () => {
            try {
                await taskRef.current(controller.signal);
            } catch {
                // Poll failures retain the last successful state and retry normally.
            } finally {
                if (!disposed && !controller.signal.aborted) {
                    timerRef.current = window.setTimeout(run, delayMs);
                }
            }
        };
        void run();
        return () => {
            disposed = true;
            controller.abort();
            if (timerRef.current !== null) {
                window.clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [enabled, delayMs, epoch]);
}
